import type { Account } from '@/interfaces/account.interface';
import { useBusinessUnitResolver } from '@/composables/useBusinessUnitResolver';
import { useCurrentAccount } from '@/composables/useCurrentAccount';
import { computed } from 'vue';

const MEXICO_COUNTRY_ID = 52;
const MEXICO_COUNTRY_CODES = new Set(['MX', 'MEX']);
const MEXICO_COUNTRY_SLUG = 'mexico';
const MEXICO_SUPPORT_WHATSAPP_PHONE = '5215594356064';
const MEXICO_SUPPORT_WHATSAPP_LABEL = '+52 1 55 9435 6064';
const MEXICO_SUPPORT_WHATSAPP_FALLBACK_URL = `https://api.whatsapp.com/send?phone=${MEXICO_SUPPORT_WHATSAPP_PHONE}`;

function isMexicoAccount(account?: Account) {
  const countryCode = account?.country?.code?.toUpperCase();
  const countrySlug = account?.country?.slug?.toLowerCase();

  return (
    account?.country_id === MEXICO_COUNTRY_ID ||
    account?.country?.id === MEXICO_COUNTRY_ID ||
    (countryCode ? MEXICO_COUNTRY_CODES.has(countryCode) : false) ||
    countrySlug === MEXICO_COUNTRY_SLUG
  );
}

function getWhatsappLinkWithPhone(whatsappUrl: string | undefined, phone: string) {
  if (!whatsappUrl) return MEXICO_SUPPORT_WHATSAPP_FALLBACK_URL;

  try {
    const url = new URL(whatsappUrl);
    url.searchParams.set('phone', phone);
    return url.toString();
  } catch {
    return MEXICO_SUPPORT_WHATSAPP_FALLBACK_URL;
  }
}

export const useSupportWhatsappLink = () => {
  const { config } = useBusinessUnitResolver();
  const { account } = useCurrentAccount();

  const isMexicoUser = computed(() => isMexicoAccount(account.value));

  const whatsappLink = computed(() => {
    if (isMexicoUser.value) {
      return getWhatsappLinkWithPhone(
        config.value?.supportWhatsappUrl,
        MEXICO_SUPPORT_WHATSAPP_PHONE,
      );
    }

    return config.value?.supportWhatsappUrl ?? '';
  });

  const whatsappLabel = computed(() =>
    isMexicoUser.value ? MEXICO_SUPPORT_WHATSAPP_LABEL : (config.value?.supportWhatsappLabel ?? ''),
  );

  return {
    whatsappLink,
    whatsappLabel,
  };
};
